import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  User, EnvelopeSimple, Phone, MapPin, IdentificationBadge, Calendar, FileText,
  DownloadSimple, Folder, ArrowRight, Sparkle, ChartBar, Warning, TrendUp,
  CurrencyDollar, Checks, ArrowUpRight,
} from "@phosphor-icons/react";

const FILING_LABELS = { single: "Single", mfj: "MFJ", mfs: "MFS", hoh: "HOH", qss: "QSS" };
const RETURN_TYPE_BADGE = {
  "1040": "bg-blue-50 text-blue-700",
  "1040NR": "bg-emerald-50 text-emerald-700",
  "1065": "bg-amber-50 text-amber-700",
  "1120": "bg-rose-50 text-rose-700",
  "1120-S": "bg-fuchsia-50 text-fuchsia-700",
  "1041": "bg-orange-50 text-orange-700",
  "FBAR": "bg-slate-100 text-slate-700",
};
const STATUS_META = {
  preparing: { label: "Preparing", tone: "bg-blue-50 text-blue-700" },
  in_review: { label: "In Review", tone: "bg-amber-50 text-amber-700" },
  waiting_client: { label: "Waiting", tone: "bg-orange-50 text-orange-700" },
  ready_signature: { label: "Ready", tone: "bg-fuchsia-50 text-fuchsia-700" },
  filed: { label: "Filed", tone: "bg-sky-50 text-sky-700" },
  accepted: { label: "Accepted", tone: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rejected", tone: "bg-rose-50 text-rose-700" },
  archived: { label: "Archived", tone: "bg-slate-100 text-slate-700" },
  draft: { label: "Draft", tone: "bg-slate-100 text-slate-700" },
};
const PAY_STATUS_META = {
  unpaid: { label: "Unpaid", tone: "bg-rose-50 text-rose-700" },
  pending: { label: "Pending", tone: "bg-amber-50 text-amber-700" },
  paid: { label: "Paid", tone: "bg-emerald-50 text-emerald-700" },
  refunded: { label: "Refunded", tone: "bg-sky-50 text-sky-700" },
  waived: { label: "Waived", tone: "bg-slate-100 text-slate-700" },
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon size={14} weight="bold" className="text-slate-400 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
        <div className="text-[#0A2540] break-words">{value}</div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone = "text-[#0A2540]" }) {
  return (
    <div className="rounded-md border tf-hairline bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
        <Icon size={14} className="text-slate-400" />
      </div>
      <div className={`mt-1.5 font-[Chivo] text-lg font-black tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

function maskSsn(s) {
  if (!s) return "";
  const clean = String(s).replace(/\D/g, "");
  if (clean.length < 4) return "•••-••-••••";
  return `•••-••-${clean.slice(-4)}`;
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function ClientDetailSheet({ open, onOpenChange, client }) {
  const navigate = useNavigate();
  const [basicInfo, setBasicInfo] = useState(null);
  const [docs, setDocs] = useState([]);
  const [returns, setReturns] = useState([]);
  const [notices, setNotices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const ownerId = client?.owner_id;

  const load = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    try {
      const [biRes, docRes, retRes, notRes, payRes] = await Promise.all([
        api.get("/basic-info").catch(() => ({ data: [] })),
        api.get("/documents").catch(() => ({ data: [] })),
        api.get("/returns").catch(() => ({ data: [] })),
        api.get("/notices").catch(() => ({ data: [] })),
        api.get("/payments").catch(() => ({ data: [] })),
      ]);
      const ownForms = (biRes.data || []).filter((f) => f.owner_id === ownerId);
      ownForms.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
      setBasicInfo(ownForms[0] || null);
      setDocs((docRes.data || []).filter((d) => d.owner_id === ownerId));
      const rows = (retRes.data || []).filter((r) => r.owner_id === ownerId);
      rows.sort((a, b) => (b.tax_year || 0) - (a.tax_year || 0));
      setReturns(rows);
      setNotices((notRes.data || []).filter((n) => n.owner_id === ownerId));
      setPayments((payRes.data || []).filter((p) => p.owner_id === ownerId));
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const analysis = useMemo(() => {
    const totalRefund = returns.reduce((s, r) => s + Number(r.refund_amount || 0), 0);
    const totalBalance = returns.reduce((s, r) => s + Number(r.balance_due || 0), 0);
    const totalLiability = returns.reduce((s, r) => s + Number(r.tax_liability || 0), 0);
    const openNotices = notices.filter((n) => n.status === "open").length;
    const filedCount = returns.filter((r) => r.status === "filed" || r.status === "accepted").length;
    const inProgress = returns.filter((r) => !["filed", "accepted", "rejected", "archived"].includes(r.status)).length;
    const avgProgress = returns.length === 0
      ? 0
      : Math.round(returns.reduce((s, r) => s + Number(r.progress || 0), 0) / returns.length);
    const paidTotal = payments.filter((p) => p.status === "succeeded").reduce((s, p) => s + Number(p.amount || 0), 0);
    const yearsFiled = [...new Set(returns.map((r) => r.tax_year).filter(Boolean))].sort((a, b) => b - a);
    const primaryType = returns[0]?.return_type || "—";
    return { totalRefund, totalBalance, totalLiability, openNotices, filedCount, inProgress, avgProgress, paidTotal, yearsFiled, primaryType };
  }, [returns, notices, payments]);

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API_BASE}/documents/${doc.id}/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    }
  };

  const fullAddress = basicInfo && [basicInfo.street, basicInfo.city, basicInfo.state, basicInfo.zip_code, basicInfo.country]
    .filter(Boolean).join(", ");

  const openReturnRow = (r) => {
    onOpenChange(false);
    navigate(`/dashboard/returns?open=${r.id}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="client-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[#0A2540]">
            <User size={18} weight="fill" /> {client?.client_name || client?.owner_name || "Client"}
          </SheetTitle>
          <SheetDescription>
            360° view — profile, tax return history, documents, notices and payments.
          </SheetDescription>
        </SheetHeader>

        {loading && <div className="py-10 text-center text-slate-500 text-sm">Loading client data…</div>}

        {!loading && (
          <div className="mt-6 space-y-6">
            {/* CLIENT ANALYSIS */}
            <section data-testid="client-analysis">
              <div className="flex items-center gap-2 mb-3">
                <ChartBar size={16} weight="fill" className="text-[#D32F2F]" />
                <h3 className="font-[Chivo] font-black text-[#0A2540]">Client Analysis</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <StatTile icon={FileText} label="Total Returns" value={returns.length} />
                <StatTile icon={Checks} label="Filed / Accepted" value={analysis.filedCount} tone="text-emerald-700" />
                <StatTile icon={TrendUp} label="In Progress" value={analysis.inProgress} tone="text-blue-700" />
                <StatTile icon={CurrencyDollar} label="Total Refunds" value={money(analysis.totalRefund)} tone="text-emerald-700" />
                <StatTile icon={CurrencyDollar} label="Balance Due" value={money(analysis.totalBalance)} tone="text-rose-700" />
                <StatTile icon={Warning} label="Open Notices" value={analysis.openNotices} tone={analysis.openNotices > 0 ? "text-rose-700" : "text-[#0A2540]"} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {analysis.yearsFiled.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-700 px-2 py-1">
                    <Calendar size={12} /> Years: <strong className="font-mono">{analysis.yearsFiled.slice(0, 5).join(" · ")}</strong>
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-700 px-2 py-1">
                  Primary type: <strong className="font-mono">{analysis.primaryType}</strong>
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-700 px-2 py-1">
                  Avg progress: <strong className="font-mono">{analysis.avgProgress}%</strong>
                </span>
                {analysis.paidTotal > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 px-2 py-1">
                    Fees paid: <strong className="font-mono">{money(analysis.paidTotal)}</strong>
                  </span>
                )}
              </div>
            </section>

            {/* TAX RETURN HISTORY (mini-table) */}
            <section data-testid="client-returns" className="rounded-md border tf-hairline bg-white overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b tf-hairline bg-slate-50/70">
                <div className="flex items-center gap-2">
                  <FileText size={16} weight="fill" className="text-[#0A2540]" />
                  <div className="font-[Chivo] font-bold text-[#0A2540]">Tax Return History</div>
                </div>
                <span className="text-xs font-mono text-slate-500">{returns.length} return{returns.length === 1 ? "" : "s"}</span>
              </div>
              {returns.length === 0 ? (
                <div className="p-6 text-sm text-slate-500 text-center">No tax returns on file for this client yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left p-2">Year</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Filing</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Progress</th>
                        <th className="text-left p-2">Assigned</th>
                        <th className="text-right p-2">Refund</th>
                        <th className="text-right p-2">Balance</th>
                        <th className="text-right p-2">Fee</th>
                        <th className="text-left p-2">Pay</th>
                        <th className="text-left p-2">Due</th>
                        <th className="text-left p-2">Updated</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {returns.map((r) => {
                        const meta = STATUS_META[r.status] || { label: r.status, tone: "bg-slate-100 text-slate-700" };
                        const typeTone = RETURN_TYPE_BADGE[r.return_type] || "bg-slate-100 text-slate-700";
                        return (
                          <tr key={r.id} className="border-t tf-hairline hover:bg-slate-50/70" data-testid={`client-return-${r.id}`}>
                            <td className="p-2 font-mono">{r.tax_year}</td>
                            <td className="p-2"><span className={`inline-block text-[10px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${typeTone}`}>{r.return_type}</span></td>
                            <td className="p-2 text-[10px] font-mono uppercase tracking-widest text-slate-600">{r.filing_status || "—"}</td>
                            <td className="p-2"><span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>{meta.label}</span></td>
                            <td className="p-2">
                              <div className="flex items-center gap-1.5 w-20">
                                <div className="flex-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                                  <div className="h-full bg-[#0A2540]" style={{ width: `${Math.max(0, Math.min(100, Number(r.progress) || 0))}%` }} />
                                </div>
                                <span className="text-[10px] font-mono text-slate-600 tabular-nums w-7 text-right">{Number(r.progress) || 0}%</span>
                              </div>
                            </td>
                            <td className="p-2 text-slate-600 text-[11px] truncate max-w-[100px]">{r.assigned_to_name || "—"}</td>
                            <td className="p-2 text-right font-mono text-emerald-700 tabular-nums">{r.refund_amount ? money(r.refund_amount) : "—"}</td>
                            <td className="p-2 text-right font-mono text-rose-700 tabular-nums">{r.balance_due ? money(r.balance_due) : "—"}</td>
                            <td className="p-2 text-right font-mono tabular-nums text-[#0A2540]">{r.fee ? money(r.fee) : "—"}</td>
                            <td className="p-2">
                              {(() => {
                                const pm = PAY_STATUS_META[r.payment_status || "unpaid"];
                                return <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${pm.tone}`}>{pm.label}</span>;
                              })()}
                            </td>
                            <td className="p-2 font-mono text-[10px] whitespace-nowrap">{r.due_date || "—"}</td>
                            <td className="p-2 font-mono text-[10px] text-slate-500 whitespace-nowrap">{(r.updated_at || r.created_at || "").split("T")[0]}</td>
                            <td className="p-2 text-right">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openReturnRow(r)} data-testid={`open-return-${r.id}`}><ArrowUpRight size={12} /></Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* PROFILE SUMMARY */}
            <section className="rounded-md border tf-hairline bg-slate-50/70 p-4 space-y-3" data-testid="client-profile">
              <div className="flex items-baseline justify-between">
                <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Client Profile</div>
                {basicInfo?.tax_year && (
                  <div className="text-xs font-mono text-slate-500">TY {basicInfo.tax_year}</div>
                )}
              </div>
              {basicInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <InfoRow icon={User} label="Full Name" value={basicInfo.full_name || client?.client_name || client?.owner_name} />
                  <InfoRow icon={EnvelopeSimple} label="Email" value={basicInfo.email} />
                  <InfoRow icon={Phone} label="Phone" value={basicInfo.phone} />
                  <InfoRow icon={IdentificationBadge} label="SSN / ITIN" value={maskSsn(basicInfo.ssn_or_itin)} />
                  <InfoRow icon={Calendar} label="Date of Birth" value={basicInfo.dob} />
                  <InfoRow icon={FileText} label="Occupation" value={basicInfo.occupation} />
                  <InfoRow icon={IdentificationBadge} label="Filing Status" value={FILING_LABELS[basicInfo.filing_status] || basicInfo.filing_status} />
                  <InfoRow icon={MapPin} label="Address" value={fullAddress} />
                  {basicInfo.spouse_name && (
                    <div className="md:col-span-2">
                      <InfoRow icon={User} label="Spouse" value={`${basicInfo.spouse_name}${basicInfo.spouse_occupation ? ` · ${basicInfo.spouse_occupation}` : ""}`} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-amber-500" />
                  This client has not submitted their Basic Info form yet.
                </div>
              )}
              {basicInfo && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {basicInfo.foreign_bank_accounts && <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-50 text-amber-700 px-2 py-0.5 rounded">FBAR flag</span>}
                  {basicInfo.crypto_transactions && <span className="text-[10px] font-mono uppercase tracking-widest bg-purple-50 text-purple-700 px-2 py-0.5 rounded">Crypto</span>}
                  {basicInfo.irs_notice_pending && <span className="text-[10px] font-mono uppercase tracking-widest bg-rose-50 text-rose-700 px-2 py-0.5 rounded">IRS notice</span>}
                  {basicInfo.health_insurance && <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">Health ins.</span>}
                  {(basicInfo.dependents || []).length > 0 && <span className="text-[10px] font-mono uppercase tracking-widest bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{basicInfo.dependents.length} dependent{basicInfo.dependents.length === 1 ? "" : "s"}</span>}
                </div>
              )}
            </section>

            {/* DOCUMENTS */}
            <section className="rounded-md border tf-hairline bg-white overflow-hidden" data-testid="client-documents">
              <div className="flex items-center justify-between px-4 py-3 border-b tf-hairline bg-slate-50/70">
                <div className="flex items-center gap-2">
                  <Folder size={16} weight="fill" className="text-[#0A2540]" />
                  <div className="font-[Chivo] font-bold text-[#0A2540]">Attached Documents</div>
                </div>
                <span className="text-xs font-mono text-slate-500">{docs.length} file{docs.length === 1 ? "" : "s"}</span>
              </div>
              {docs.length === 0 && (
                <div className="p-6 text-sm text-slate-500 text-center">No documents uploaded by this client yet.</div>
              )}
              {docs.length > 0 && (
                <ul className="divide-y tf-hairline">
                  {docs.map((d) => (
                    <li key={d.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/70" data-testid={`client-doc-${d.id}`}>
                      <FileText size={16} className="text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0A2540] truncate">{d.filename}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                          {d.category} · {(d.size / 1024).toFixed(1)} KB · {(d.uploaded_at || "").split("T")[0]}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => download(d)} data-testid={`sheet-download-${d.id}`}>
                        <DownloadSimple size={15} className="text-[#0A2540]" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="px-4 py-3 border-t tf-hairline bg-slate-50/70 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onOpenChange(false); navigate(`/dashboard/documents?client=${encodeURIComponent(client?.owner_name || "")}`); }}
                  data-testid="open-docs-page"
                >
                  Open in Documents <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
